import { CSSProperties, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { useFeatureFlags, useLanguage, usePermissionFlags } from '../../hooks';
import { ChevronRightOutline } from '../../icons';
import { SettingsOutline } from '../../icons/SettingsOutline';
import { useMainLayoutStore } from '../../newStore/mainLayoutStore';
import { useUserDataStore } from '../../newStore/userDataStore';
import { commonStyles } from '../../style/CommonStyles';
import { paths } from '../../utils/navigation';
import { isSupportOrAdmin } from '../../utils/utils';

const customStyles: Record<string, SxProps<Theme>> = {
  settingsContainer: {
    padding: '4px 0px',
  },
  settingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  settingsButton: {
    color: 'inherit',
    padding: '6px 12px',
    height: '34px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    gap: '10px',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    minWidth: 'unset',
    '&:hover': {
      backgroundColor: '#6DB1FB',
    },
    '& .MuiButton-endIcon': {
      color: '#FFFFFF',
      marginLeft: '0px',
    },
  },
  settingsIcon: {
    width: '20px',
    height: '20px',
  },
  settingsPopover: {
    marginLeft: '15px',
    '& .MuiPopover-paper': {
      backgroundColor: (theme) => theme.palette.primary.main,
      borderRadius: '12px',
      boxShadow: '2px 4px 16px 0px rgba(15, 59, 96, 0.18)',
    },
  },
  settingsPopoverContent: {
    padding: '8px',
    width: '158px',
  },
  settingsMenuItem: {
    padding: '6px 12px',
    height: '30px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#6DB1FB',
    },
    '& .MuiTypography-root': {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 400,
      color: '#FFFFFF',
    },
  },
  chevronRightIcon: {
    color: '#FFFFFF',
    width: '20px',
    height: '20px',
    position: 'absolute',
    right: '0px',
  },
};

export function SettingsMenu() {
  const lang = useLanguage();
  const location = useLocation();
  const currentUser = useUserDataStore((state) => state.currentUser);

  const expandedDesktop = useMainLayoutStore((state) => state.expandedDesktop);

  const { checkFlag, loading: flagsLoading } = useFeatureFlags();
  const { checkPermission } = usePermissionFlags();
  const hasFullAccess = isSupportOrAdmin(currentUser);
  const paymentsV2Enabled = checkFlag('PaymentsV2');

  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLButtonElement | null>(null);

  const settingsItems = [
    {
      label: lang.mainMenu.settings.account,
      path: paths.account.index,
      hide:
        !checkPermission('users') &&
        !checkPermission('metadata') &&
        !checkPermission('admin_account_config') &&
        !hasFullAccess,
    },
    {
      label: lang.mainMenu.settings.users,
      path: paths.users.index,
      hide: !checkPermission('users') && !hasFullAccess,
    },
    {
      label: lang.mainMenu.settings.billing,
      path: paths.billing.index,
      hide: !checkPermission('admin_account_config') && !hasFullAccess,
      link:
        paymentsV2Enabled || flagsLoading
          ? undefined
          : `${process.env.REACT_APP_BACKEND_ENDPOINT}/billing`,
    },
    {
      label: lang.mainMenu.lists,
      path: paths.lists.index,
      hide: !checkPermission('metadata') && !hasFullAccess,
    },
    {
      label: lang.mainMenu.settings.invite,
      link: `${process.env.REACT_APP_BACKEND_ENDPOINT}/invite`,
    },
  ];
  const isActive = settingsItems.some((item) => location.pathname === item.path);

  return (
    <Box sx={customStyles.settingsContainer}>
      <Tooltip
        title={lang.mainMenu.settings.main}
        placement='right'
        arrow
        disableHoverListener={expandedDesktop}
      >
        <Button
          fullWidth
          variant='text'
          sx={{
            ...customStyles.settingsButton,
            ...(!expandedDesktop && {
              justifyContent: 'center',
            }),
            ...(isActive && {
              backgroundColor: '#6DB1FB',
            }),
          }}
          onClick={(event) => setSettingsAnchorEl(event.currentTarget)}
        >
          <SettingsOutline sx={customStyles.settingsIcon} />
          {expandedDesktop && lang.mainMenu.settings.main}
          <ChevronRightOutline
            sx={{
              ...customStyles.chevronRightIcon,
              ...(!expandedDesktop && {
                display: 'none',
              }),
            }}
          />
        </Button>
      </Tooltip>
      <Popover
        open={!!settingsAnchorEl}
        onClose={() => setSettingsAnchorEl(null)}
        anchorEl={settingsAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={customStyles.settingsPopover}
      >
        <Box sx={customStyles.settingsPopoverContent}>
          <List
            component='nav'
            sx={customStyles.settingsList}
          >
            {settingsItems
              .filter((item) => !item.hide)
              .map((item) =>
                item.link ? (
                  <Typography
                    key={item.label}
                    component='a'
                    href={item.link}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={commonStyles.linkOverride as CSSProperties}
                    onClick={() => setSettingsAnchorEl(null)}
                  >
                    <ListItemButton sx={customStyles.settingsMenuItem}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </Typography>
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.path}
                    onClick={() => setSettingsAnchorEl(null)}
                    style={commonStyles.linkOverride as CSSProperties}
                  >
                    {({ isActive }) => (
                      <ListItemButton
                        sx={{
                          ...customStyles.settingsMenuItem,
                          ...(isActive && {
                            backgroundColor: '#6DB1FB',
                          }),
                        }}
                      >
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    )}
                  </NavLink>
                ),
              )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
}
